import type { QueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

/**
 * Everything derived from the stored Battery price, except the pricing read
 * itself — a command that returns pricing owns that answer and must not have
 * it refetched out from under the response it just wrote.
 *
 * The commands that save a Loan Product also move the pricing-input
 * generation, so a failure leaves the same set suspect as a success; both
 * paths refresh all of it.
 */
export const invalidateBatteryPriceDependents = (
  queryClient: QueryClient,
  proposalId: string,
): void => {
  // Prefix match: the Concert and LightReach offers reads hang off this key too.
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.financing(proposalId),
  })
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.financialSummary(proposalId),
  })
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.incentives(proposalId),
  })
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.contract(proposalId),
  })
  // Concert initiation/submission capabilities follow Quote freshness.
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.concertApplication(proposalId),
  })
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.sungageApplications(proposalId),
  })
  // Publication readiness is the same freshness answer, so a price move
  // decides whether Share can publish.
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.presentationLink(proposalId),
  })
  // proposalCost on the render contract gates downstream menu items.
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.byId(proposalId),
    exact: true,
  })
}

/** For commands that move the stored price without returning it. */
export const invalidateBatteryPrice = (
  queryClient: QueryClient,
  proposalId: string,
): void => {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.batteryProposal.pricing(proposalId),
  })
  invalidateBatteryPriceDependents(queryClient, proposalId)
}
